/**
 * 📚 **AR 목록을 책별로 묶는다.**
 *
 * ## 왜
 * 목록이 **한 줄로 쭉** 늘어서 있었다. 그런데 장면 id 는 두 갈래다:
 *  · 전자책 페이지 — `<책id>.<쪽id>` (책 제목이 장면 제목이라 **같은 제목이 쪽 수만큼 반복**된다)
 *  · 경주 카드 — `ar_chumseongdae` 같은 낱장(관광객이 실물 카드에 카메라를 비춘다)
 *
 * 34쪽짜리 책에 AR 을 다 걸면 「그레그, 경주로 폴짝!」이 서른네 번 나오고 그 사이에 경주 카드가
 * 섞인다. 아이도 보호자도 무엇을 고르는 건지 알 수 없다.
 *
 * ## 무엇을 없애지 않는다
 * 경주 카드는 지우지 않는다 — 실물 카드를 든 관광객이 쓰는 길이다. **따로 묶어서** 보여 준다.
 *
 * ## 판정을 여기 두는 이유
 * 「이 장면이 어느 책 것인가」를 화면에서 다시 짜면, 관리자·웹이 갈린다.
 * id 규칙(`<책>.<쪽>`)은 `scenesFromManifest` 가 만들고 여기서 되읽는다 — 한 곳에 붙여 둔다.
 */
import { configuredUrl } from "./placeholder";

/** 묶는 데 필요한 만큼의 장면. 뷰어의 `ArScene` 을 그대로 넣어도 된다. */
export interface ArGroupInput {
  id: string;
  title_ko: string;
  /** 카메라가 알아볼 그림(.mind). 없으면 **트래킹이 아예 안 된다**. */
  targetSrc?: string;
  /** 카메라 대신 볼 영상. 미설정(REPLACE)·로컬주소는 `arSceneReady` 가 여기서도 본다. */
  fallback_youtube?: string;
  /**
   * 📖 책에서 몇 번째 쪽인가(1부터, 표지 포함). **목록 차례를 이걸로 정한다.**
   * 모르면(책에 없는 떠돌이 타깃) 없어도 된다 — 뒤로 밀린다.
   */
  pageNo?: number;
}

/**
 * 🔴 **눌러서 무언가 일어나는 장면인가.**
 *
 * 목록에 있는데 아무 일도 안 일어나는 칸이 있었다. 경주 카드 6장은 `targetSrc` 가 비어 있고
 * 영상도 `REPLACE` 라, 누르면 카메라만 켜지고 「트래킹 타깃은 준비 중이에요」로 끝났다.
 * 아이는 여섯 번 눌러 보고 여섯 번 아무것도 못 본다 — **막다른 길**이다(2026-08-23 요청).
 *
 * 볼 것이 있는 두 경우만 보여 준다:
 *  · 카메라가 알아볼 그림이 있다 → 진짜 AR
 *  · 대신 볼 영상이 있다 → 카메라가 안 돼도 볼 수 있다(FR-AR-04)
 *
 * 재료(연속그림·포스터)만 있는 건 **보여 주지 않는다.** 그건 아직 준비 중인 것이고,
 * 운영자에게는 출시 준비 목록이 「AR 재료만 있고 카메라가 못 알아보는 쪽」으로 알려 준다.
 *
 * 🔴 **아직 안 채운 주소(`REPLACE`)는 「볼 것」이 아니다.**
 * 위 설명이 「영상도 REPLACE 라」라고 적어 두고도, 코드는 **길이만** 봤다. 지금 안 깨지는 건
 * 부르는 쪽(`arData`)이 먼저 `configuredUrl` 로 걸러 주기 때문인데 — 그건 **다른 파일의
 * 순서에 기대는 것**이다. 관리자 목록이든 새 부르는 자리든, 날 것을 그대로 넘기는 순간
 * 자리표시자 장면이 다시 목록에 서고 아이는 눌러도 아무것도 못 본다.
 * 판정이 제 설명대로 하게 여기서 본다.
 */
export function arSceneReady(s: ArGroupInput): boolean {
  if (s.targetSrc && s.targetSrc.length > 0) return true;
  return !!configuredUrl(s.fallback_youtube);
}

export interface ArBookGroup<T extends ArGroupInput> {
  bookId: string;
  /** 화면에 쓸 책 제목. 장면 제목이 곧 책 제목이라 첫 장면에서 가져온다. */
  title: string;
  scenes: T[];
}

export interface ArGrouped<T extends ArGroupInput> {
  /** 전자책별 묶음 — 책 안에서는 원래 차례를 지킨다(쪽 순서다). */
  books: ArBookGroup<T>[];
  /** 책에 속하지 않는 낱장(경주 카드 등). */
  others: T[];
}

/**
 * 장면 id 에서 책 id 를 꺼낸다. `<책>.<쪽>` 이면 앞부분, 점이 없으면 책이 아니다.
 *
 * 🔴 **마지막 점**으로 자른다 — 책 id 에 점이 들어갈 수 있다(`scenesFromManifest` 와 같은 규칙).
 * 첫 점으로 자르면 `my.book.p01` 의 책이 `my` 가 되어 한 책이 둘로 갈린다.
 */
export function arSceneBookId(id: string): string | undefined {
  const dot = id.lastIndexOf(".");
  if (dot <= 0 || dot === id.length - 1) return undefined;
  return id.slice(0, dot);
}

/** 책별로 묶는다. 책이 나온 차례·책 안의 차례 모두 **들어온 순서 그대로**(쪽 순서를 뒤집지 않는다). */
export function groupArScenes<T extends ArGroupInput>(scenes: readonly T[]): ArGrouped<T> {
  const books = new Map<string, ArBookGroup<T>>();
  const others: T[] = [];
  for (const s of scenes) {
    // 볼 것이 없는 장면은 아예 안 넣는다 — 부르는 쪽이 거르는 걸 잊으면 막다른 길이 돌아온다
    if (!arSceneReady(s)) continue;
    const bookId = arSceneBookId(s.id);
    if (!bookId) {
      others.push(s);
      continue;
    }
    const cur = books.get(bookId);
    if (cur) cur.scenes.push(s);
    else books.set(bookId, { bookId, title: s.title_ko, scenes: [s] });
  }
  /**
   * 🔴 **쪽 차례대로 보여 준다** (2026-09-10, 아이 화면을 열어 보고 알았다).
   *
   * 자리 목록(매니페스트)에 들어간 순서 그대로 내놓고 있었다 — 운영에서 실제로
   * **표지·8·20·22·23·12·27·30·7쪽** 으로 뒤섞여 나왔다. 보호자가 「12쪽 비추자」 하고
   * 목록을 훑으면 눈이 헤맨다. 책은 **차례가 있는 물건**이다.
   *
   * 쪽 번호를 모르는 장면(책에 없는 떠돌이 타깃)은 **뒤로** 민다 — 지어내서 끼우지 않는다.
   * 같은 자리끼리는 원래 순서를 지킨다(정렬이 흔들리면 열 때마다 목록이 달라 보인다).
   */
  for (const b of books.values()) {
    b.scenes = b.scenes
      .map((s, i) => ({ s, i }))
      .sort((a, z) => {
        const an = a.s.pageNo ?? Number.POSITIVE_INFINITY;
        const zn = z.s.pageNo ?? Number.POSITIVE_INFINITY;
        return an - zn || a.i - z.i;
      })
      .map(({ s }) => s);
  }
  return { books: [...books.values()], others };
}

/** 장면 id 에서 쪽 id 를 꺼낸다(`poljjak.p07` → `p07`). 점이 없으면 통째로 쪽 id 다. */
export function arPageIdOf(sceneId: string): string {
  const dot = sceneId.lastIndexOf(".");
  return dot > 0 ? sceneId.slice(dot + 1) : sceneId;
}

/**
 * 📖 **책에서 몇 번째 쪽인가** — 책 매니페스트의 차례를 그대로 센다(1부터, 표지 포함).
 *
 * 🔴 뷰어(`useBookViewer`)도 관리자 책 편집기도 **차례**로 센다(`indexOf(page) + 1`).
 * 여기서만 따로 세면 같은 쪽을 두 이름으로 부르게 된다. 그래서 **세는 일을 안 한다** —
 * 뷰어가 쓰는 그 배열을 받아서 그 안의 자리를 돌려줄 뿐이다.
 *
 * 책에 없는 쪽(떠돌이 AR 타깃)이면 `undefined` — **모르는 것은 모른다고 한다.**
 */
export function arPageNo(pageIds: readonly string[], sceneId: string): number | undefined {
  const i = pageIds.indexOf(arPageIdOf(sceneId));
  return i >= 0 ? i + 1 : undefined;
}

/**
 * 장면 id 뒤쪽(쪽 id)을 **사람 말**로. `cover` → 「표지」, 차례 8번째 → 「8쪽」.
 *
 * 예전 목록엔 「cover 페이지를 비춰보세요」가 그대로 떴다 — `cover` 는 개발자 말이다.
 *
 * 🔴 **쪽 번호를 id 에서 뽑지 않는다**(2026-09-03에 고쳤다). `p07` 을 「7쪽」이라 불렀는데,
 * 같은 쪽을 뷰어는 「8 / 34」·관리자 책 편집기는 「8쪽」이라 부르고 있었다 — **표지가
 * 앞에 한 장 있어서** 차례와 id 숫자가 한 칸씩 어긋난다. 그래서 「7쪽을 비춰보세요」는
 * 아이가 어디에서도 찾을 수 없는 번호였다. 번호는 `pageNo`(책 매니페스트의 차례)로만 말한다.
 *
 * 차례를 모르면(책을 못 읽었거나 책에 없는 쪽) **번호를 지어내지 않고** 자리 이름을 그대로
 * 보여 준다 — 운영자에겐 「이 쪽은 책에 없다」는 뜻이 된다.
 */
export function arPageLabel(sceneId: string, pageNo?: number): string {
  const pageId = arPageIdOf(sceneId);
  if (pageId === "cover") return "표지";
  if (Number.isInteger(pageNo) && (pageNo as number) >= 1) return `${pageNo}쪽`;
  return pageId;
}

/**
 * 📷 **무엇을 비추면 되는지** — 목록과 체험 화면이 **같은 말**을 쓰게.
 *
 * 🔴 예전엔 목록은 「표지」·「8쪽」이라 하고, 정작 카메라 화면은 **「cover 페이지를
 * 비춰보세요」** 라고 했다. 자리 이름(`cover`·`p07`)을 그대로 화면에 내보낸 것이다 —
 * 영유아·보호자용 한글 화면에 영어 자리 이름이 나오고, 같은 것을 두 이름으로 부른다.
 * 이름 짓는 일은 `arPageLabel` 한 곳에 있었는데 여기서만 손으로 이어 붙이고 있었다.
 *
 * 받침에 따라 「을/를」이 갈린다 — 「표지**를**」 · 「8쪽**을**」.
 */
export function arAimSay(sceneId: string, pageNo?: number): string {
  const label = arPageLabel(sceneId, pageNo);
  const last = label.charCodeAt(label.length - 1);
  // 한글 음절이면 받침 유무로, 아니면 안전하게 「를」
  const hangul = last >= 0xac00 && last <= 0xd7a3;
  const josa = hangul && (last - 0xac00) % 28 !== 0 ? "을" : "를";
  return `${label}${josa} 비춰보세요`;
}

/**
 * 👻 **책에서 사라진 쪽에 걸린 AR 타깃을 센다.**
 *
 * 책은 살아 있는데 그 **쪽만** 없는 경우다 — 관리자에서 쪽을 지우거나 쪽 id 를 바꾸면
 * `ar.target.<책>.<쪽>` 은 매니페스트에 그대로 남는다. 「타깃이 없다」(`arNoTarget`)도
 * 「책이 없다」(`bookOrphanAssets`)도 아닌 **세 번째 경우**라 아무 데도 안 세고 있었다.
 *
 * 🔴 아이 화면에 그대로 나간다. 쪽 번호는 책 차례에서 오는데(`arPageNo`) 그 쪽이 책에
 * 없으면 번호를 지어내지 않으므로 목록에 **「p07」**, 체험 화면에 **「p07를 비춰보세요」**
 * 가 뜬다. 영유아·보호자용 한글 화면에서. 게다가 그 타깃은 어느 쪽 그림과도 안 맞으니
 * 카메라는 영영 아무것도 못 알아본다.
 *
 * 🔴 **본문을 못 받은 책은 세지 않는다.** 못 받은 것을 「쪽이 없다」로 읽으면 멀쩡한 AR 을
 * 전부 유령으로 신고한다 — 화면이 처음 뜰 때 흔한 상태다.
 *
 * @param sceneIds 웹에 나가 있는 `ar.target.*` 의 장면 id 들(`<책>.<쪽>`)
 * @param pagesOf  그 책의 쪽 id 들. **본문을 받은 책만** 넣는다(못 받았으면 넣지 않는다).
 */
export function arPagesGone(
  sceneIds: readonly string[],
  pagesOf: (bookId: string) => readonly string[] | undefined,
): string[] {
  const gone: string[] = [];
  for (const id of sceneIds) {
    const bookId = arSceneBookId(id);
    if (!bookId) continue; // 경주 카드(점 없음) — 책 쪽이 아니다
    const known = pagesOf(bookId);
    if (!known) continue; // 본문을 못 받은 책 — 판단하지 않는다
    if (!known.includes(arPageIdOf(id))) gone.push(id);
  }
  return gone;
}
